import { ApiError, endpoints } from '@gopinhos/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { apiClient } from '../lib/api-client';

const COLOR_PRESETS = ['#1F3A5F', '#2563EB', '#7C3AED', '#059669', '#DC2626', '#D97706', '#0891B2'];

export function ProjectCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [color, setColor] = useState(COLOR_PRESETS[0]!);

  return (
    <div className="mx-auto max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>{t('projectCreate.title')}</CardTitle>
          <p className="text-sm text-surface-500">{t('projectCreate.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setSubmitting(true);
              setError(null);
              const fd = new FormData(e.currentTarget);
              try {
                const project = (await apiClient.request({
                  method: 'POST',
                  path: endpoints.projects(),
                  body: {
                    name: String(fd.get('name') ?? '').trim(),
                    description: String(fd.get('description') ?? '').trim() || undefined,
                    color,
                    seedFlowchart: String(fd.get('seedFlowchart') ?? 'default-sales') as
                      | 'default-sales'
                      | 'empty',
                  },
                })) as { id: string };
                navigate(`/projects/${project.id}`);
              } catch (err) {
                setError(err instanceof ApiError ? err.problem.title : t('projectCreate.errorGeneric'));
              } finally {
                setSubmitting(false);
              }
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="name">{t('projectCreate.name')}</Label>
              <Input id="name" name="name" required maxLength={120} placeholder={t('projectCreate.namePlaceholder')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description">{t('projectCreate.description')}</Label>
              <Textarea
                id="description"
                name="description"
                maxLength={2000}
                placeholder={t('projectCreate.descriptionPlaceholder')}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('projectCreate.color')}</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    aria-label={c}
                    className={`h-8 w-8 rounded-full ring-offset-2 ${color === c ? 'ring-2 ring-surface-900' : ''}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seedFlowchart">{t('projectCreate.starterFlowchart')}</Label>
              <select
                id="seedFlowchart"
                name="seedFlowchart"
                defaultValue="default-sales"
                className="flex h-10 w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm focus-ring"
              >
                <option value="default-sales">{t('projectCreate.flowchartDefaultSales')}</option>
                <option value="empty">{t('projectCreate.flowchartEmpty')}</option>
              </select>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" type="button" onClick={() => navigate(-1)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? t('common.creating') : t('projectCreate.createButton')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
